import React from 'react';
import { todayAssignmentsAssets } from '@/assets/images';
import { Button } from '@/shared/components/ui/Button';

export interface Assignment {
  id: string;
  patient_name: string;
  age: number;
  registration_time: string;
  token_number: string;
  doctor_name: string;
  department: string;
  status: 'inprogress' | 'waiting' | 'called' | 'completed';
  is_priority: boolean;
  estimated_wait_minutes: number;
}

interface PatientQueueListProps {
  assignments: Assignment[];
  onTogglePriority: (assignmentId: string) => void;
}

const TitleSection: React.FC = () => (
  <div className="content-stretch flex flex-col gap-[4px] items-start relative size-full">
    <div className="content-stretch flex gap-[8px] items-center relative shrink-0">
      <div className="relative shrink-0 size-[24px]">
        <img alt="" className="block max-w-none size-full" src={todayAssignmentsAssets.profileIcon} />
      </div>
      <div className="font-['Roboto'] font-semibold leading-[0] relative shrink-0 text-[var(--color-text-primary)] text-[24px] text-nowrap" style={{ fontVariationSettings: "'wdth' 100" }}>
        <p className="leading-[32px] whitespace-pre">Patients in Queue</p>
      </div>
    </div>
    <div className="content-stretch flex gap-[10px] items-center justify-center relative shrink-0 w-full">
      <div className="basis-0 font-['Roboto'] font-normal grow leading-[0] min-h-px min-w-px opacity-50 relative shrink-0 text-[var(--color-text-primary)] text-[16px] tracking-[0.5px]" style={{ fontVariationSettings: "'wdth' 100" }}>
        <p className="leading-[24px]">Current patients waiting for services</p>
      </div>
    </div>
  </div>
);

const ChevronDownIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const DoctorCard: React.FC<{ assignment: Assignment }> = ({ assignment }) => (
  <div className="box-border content-stretch flex gap-[12px] items-center px-[12px] py-[8px] relative rounded-[6px] size-full border border-[#d2d2d2]">
    <div className="content-stretch flex flex-col items-center justify-center relative rounded-[500px] shrink-0 size-[38px]">
      <img alt="" className="absolute inset-0 max-w-none object-50%-50% object-cover pointer-events-none rounded-[500px] size-full" src={todayAssignmentsAssets.avatar} />
    </div>
    <div className="content-stretch flex flex-col font-['Roboto'] font-normal items-start leading-[0] relative shrink-0 text-[var(--color-text-primary)] w-[139px]">
      <div className="relative shrink-0 text-[16px] tracking-[0.5px] w-full" style={{ fontVariationSettings: "'wdth' 100" }}>
        <p className="leading-[24px]">{assignment.doctor_name}</p>
      </div>
      <div className="opacity-50 relative shrink-0 text-[14px] tracking-[0.25px] w-full" style={{ fontVariationSettings: "'wdth' 100" }}>
        <p className="leading-[20px]">{assignment.department}</p>
      </div>
    </div>
    <div className="relative shrink-0 size-[24px]">
      <img alt="" className="block max-w-none size-full" src={todayAssignmentsAssets.messageIcon} />
    </div>
  </div>
);

const StatusBadge: React.FC<{ status: Assignment['status']; assignment: Assignment }> = ({ status, assignment }) => {
  const statusConfig = {
    inprogress: {
      label: 'In Progress',
      bgColor: 'bg-[rgba(0,131,202,0.2)]',
      textColor: 'text-[#005a8c]'
    },
    waiting: {
      label: 'Waiting',
      bgColor: 'bg-[rgba(255,159,67,0.2)]',
      textColor: 'text-[#cc7a00]'
    },
    called: {
      label: 'Called',
      bgColor: 'bg-[rgba(156,39,176,0.2)]',
      textColor: 'text-[#7b1fa2]'
    },
    completed: {
      label: 'Completed',
      bgColor: 'bg-[rgba(40,199,111,0.2)]',
      textColor: 'text-[#1a8f5a]'
    }
  };

  const formatWaitTime = (minutes: number) => {
    if (minutes === 0) return 'Now';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  };

  const config = statusConfig[status];
  const showWaitTime = status === 'waiting';

  return (
    <div className={`${config.bgColor} box-border content-stretch flex gap-[4px] items-center justify-center w-[130px] px-[12px] py-[6px] relative rounded-[6px] shrink-0`}>
      <div className={`basis-0 flex flex-col font-['Roboto'] font-medium grow justify-center leading-[0] min-h-px min-w-px relative shrink-0 ${config.textColor} text-[14px] text-center`} style={{ fontVariationSettings: "'wdth' 100" }}>
        <p className="leading-[22px]">{config.label}</p>
        {showWaitTime && (
          <p className="text-[12px] opacity-90 leading-[18px]">{formatWaitTime(assignment.estimated_wait_minutes)}</p>
        )}
      </div>
    </div>
  );
};

export const PatientQueueList: React.FC<PatientQueueListProps> = ({ assignments, onTogglePriority }) => {
  return (
    <div className="bg-white box-border content-stretch flex flex-col items-start mb-8 relative rounded-[6px] shadow-[0px_3px_12px_0px_rgba(61,49,43,0.14)] size-full">
      {/* Header Section */}
      <div className="box-border content-stretch flex gap-[24px] items-start p-[24px] relative shrink-0 w-full">
        <div className="basis-0 content-stretch flex flex-col gap-[4px] grow items-start min-h-px min-w-px relative shrink-0">
          <TitleSection />
        </div>
        <div className="bg-white content-stretch flex flex-col items-start relative shrink-0">
          <Button
            variant="neutral"
            padding="px-[16px] py-[12px]"
            className="!bg-transparent border border-[rgba(47,43,61,0.22)] w-[160px]"
          >
            <div className="content-stretch flex flex-col gap-[4px] items-start relative shrink-0 w-full">
              <div className="content-stretch flex gap-[10px] items-center relative shrink-0 w-full">
                <div className="basis-0 content-stretch flex flex-col grow items-start justify-center min-h-px min-w-px relative shrink-0">
                  <div className="content-stretch flex gap-[10px] items-start relative shrink-0">
                    <div className="flex flex-col font-['Roboto'] font-normal justify-center leading-[0] relative shrink-0 text-[17px] text-[rgba(47,43,61,0.4)] text-nowrap" style={{ fontVariationSettings: "'wdth' 100" }}>
                      <p className="leading-[24px] whitespace-pre">All status</p>
                    </div>
                  </div>
                </div>
                <div className="relative shrink-0 size-[18px]">
                  <ChevronDownIcon />
                </div>
              </div>
            </div>
          </Button>
        </div>
      </div>

      {/* Assignment Cards */}
      <div className="box-border content-stretch flex flex-col items-center pb-0 pt-[24px] px-0 relative shrink-0 w-full">
        <div className="relative shrink-0 w-full">
          <div className="border-b border-[#e4e4e4] w-full"></div>
        </div>
        <div className="box-border content-stretch flex flex-col gap-[0px] items-stretch p-[24px] relative rounded-bl-[12px] rounded-br-[12px] shrink-0 w-full">
          {assignments.map((assignment, index) => (
            <div
              key={assignment.id}
              className={`bg-white box-border content-stretch flex flex-col lg:grid lg:grid-cols-12 items-start lg:items-center gap-[24px] px-[20px] py-[24px] relative rounded-[6px] shrink-0 w-full ${index < assignments.length - 1 ? 'border-b border-[#e4e4e4]' : ''}`}
            >
              <div className="content-stretch flex flex-col gap-[4px] items-start relative shrink-0 w-full lg:col-span-4">
                <div className="content-stretch flex gap-[12px] items-center relative shrink-0 w-full">
                  <div className="font-['Roboto'] font-normal leading-[0] relative shrink-0 text-[var(--color-text-primary)] text-[24px] text-nowrap" style={{ fontVariationSettings: "'wdth' 100" }}>
                    <p className="leading-[36px] whitespace-pre">{assignment.patient_name}</p>
                  </div>
                  <div className="relative shrink-0 size-[24px]">
                    <img alt="" className="block max-w-none size-full" src={todayAssignmentsAssets.eyeIcon} />
                  </div>
                </div>
                <div className="content-stretch flex gap-[16px] items-center relative shrink-0">
                  <div className="font-['Roboto'] font-normal leading-[0] relative shrink-0 text-[var(--color-text-primary)] text-[16px] text-nowrap tracking-[0.5px]" style={{ fontVariationSettings: "'wdth' 100" }}>
                    <p className="leading-[24px] whitespace-pre">Age: {assignment.age}</p>
                  </div>
                  <div className="content-stretch flex gap-[8px] items-center relative shrink-0">
                    <div className="relative shrink-0 size-[4px]">
                      <img alt="" className="block max-w-none size-full" src={todayAssignmentsAssets.ellipse} />
                    </div>
                    <div className="font-['Roboto'] font-normal leading-[0] relative shrink-0 text-[var(--color-text-primary)] text-[16px] text-nowrap tracking-[0.5px]" style={{ fontVariationSettings: "'wdth' 100" }}>
                      <p className="leading-[24px] whitespace-pre">Registered: {assignment.registration_time}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="w-full lg:col-span-2 flex justify-center items-center">
                {assignment.is_priority ? (
                  <Button
                    onClick={() => onTogglePriority(assignment.id)}
                    variant="danger"
                    padding="px-4 py-2"
                    fontSize="text-sm"
                    className="flex items-center gap-2 !bg-red-100 !text-red-600 hover:!bg-red-200 border-2 border-red-200 font-semibold"
                    title="Click to remove priority"
                  >
                    <span className="text-lg">🔥</span>
                    PRIORITY
                  </Button>
                ) : (
                  <Button
                    onClick={() => onTogglePriority(assignment.id)}
                    variant="neutral"
                    padding="px-4 py-2"
                    fontSize="text-sm"
                    className="flex items-center gap-2 !bg-gray-50 !text-gray-500 hover:!bg-red-100 hover:!text-red-600 hover:!border-red-200 border-2 border-gray-200"
                    title="Click to mark as priority"
                  >
                    <span className="text-lg">⚪</span>
                    Mark Priority
                  </Button>
                )}
              </div>
              <div className="font-['Roboto'] font-normal leading-[0] relative shrink-0 text-[var(--color-text-primary)] text-[24px] text-nowrap w-full lg:col-span-1 text-center flex justify-center items-center" style={{ fontVariationSettings: "'wdth' 100" }}>
                <p className="leading-[36px] whitespace-pre">{assignment.token_number}</p>
              </div>
              <div className="w-full lg:col-span-3">
                <DoctorCard assignment={assignment} />
              </div>
              <div className="w-full lg:col-span-2 flex justify-center items-center">
                <StatusBadge status={assignment.status} assignment={assignment} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
